import {
  type CreateMaintenanceCustomerRequest,
  type CreateMaintenanceInstallationRequest,
  type CreateMaintenanceProjectRequest,
  type MaintenanceCustomerListQuery,
  type MaintenanceInstallationListQuery,
  type MaintenanceProjectListQuery,
  maintenanceCustomerListResponseSchema,
  maintenanceCustomerSchema,
  maintenanceInstallationHistorySchema,
  maintenanceInstallationListResponseSchema,
  maintenanceInstallationSchema,
  maintenanceProjectListResponseSchema,
  maintenanceProjectSchema,
  type UpdateMaintenanceCustomerRequest,
  type UpdateMaintenanceInstallationRequest,
  type UpdateMaintenanceProjectRequest,
} from '@gb-ticket/shared-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api';

export const maintenanceCustomersKey = (query: MaintenanceCustomerListQuery) =>
  ['maintenance', 'customers', query] as const;

export const maintenanceCustomerKey = (id: string) => ['maintenance', 'customer', id] as const;

export const maintenanceProjectsKey = (query: MaintenanceProjectListQuery) =>
  ['maintenance', 'projects', query] as const;

export const maintenanceProjectKey = (id: string) => ['maintenance', 'project', id] as const;

export const maintenanceInstallationsKey = (query: MaintenanceInstallationListQuery) =>
  ['maintenance', 'installations', query] as const;

export const maintenanceInstallationKey = (id: string) =>
  ['maintenance', 'installation', id] as const;

export function useMaintenanceCustomers(query: MaintenanceCustomerListQuery) {
  const search = new URLSearchParams();
  search.set('page', String(query.page));
  search.set('pageSize', String(query.pageSize));
  search.set('sortBy', query.sortBy);
  search.set('sortOrder', query.sortOrder);
  if (query.q) {
    search.set('q', query.q);
  }
  if (query.isActive !== undefined) {
    search.set('isActive', String(query.isActive));
  }
  return useQuery({
    queryKey: maintenanceCustomersKey(query),
    queryFn: () =>
      apiFetch(
        `/maintenance/customers?${search.toString()}`,
        maintenanceCustomerListResponseSchema,
      ),
  });
}

export function useMaintenanceCustomer(id: string) {
  return useQuery({
    queryKey: maintenanceCustomerKey(id),
    queryFn: () => apiFetch(`/maintenance/customers/${id}`, maintenanceCustomerSchema),
    enabled: Boolean(id),
  });
}

export function useCreateMaintenanceCustomer() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateMaintenanceCustomerRequest) =>
      apiFetch('/maintenance/customers', maintenanceCustomerSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ['maintenance', 'customers'] });
    },
  });
}

export function useUpdateMaintenanceCustomer(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateMaintenanceCustomerRequest) =>
      apiFetch(`/maintenance/customers/${id}`, maintenanceCustomerSchema, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ['maintenance', 'customers'] });
      await client.invalidateQueries({ queryKey: maintenanceCustomerKey(id) });
    },
  });
}

export function useMaintenanceProjects(query: MaintenanceProjectListQuery) {
  const search = new URLSearchParams();
  search.set('page', String(query.page));
  search.set('pageSize', String(query.pageSize));
  search.set('sortBy', query.sortBy);
  search.set('sortOrder', query.sortOrder);
  if (query.q) {
    search.set('q', query.q);
  }
  if (query.customerId) {
    search.set('customerId', query.customerId);
  }
  if (query.isActive !== undefined) {
    search.set('isActive', String(query.isActive));
  }
  return useQuery({
    queryKey: maintenanceProjectsKey(query),
    queryFn: () =>
      apiFetch(`/maintenance/projects?${search.toString()}`, maintenanceProjectListResponseSchema),
    enabled: query.customerId === undefined || Boolean(query.customerId),
  });
}

export function useMaintenanceProject(id: string) {
  return useQuery({
    queryKey: maintenanceProjectKey(id),
    queryFn: () => apiFetch(`/maintenance/projects/${id}`, maintenanceProjectSchema),
    enabled: Boolean(id),
  });
}

export function useCreateMaintenanceProject() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateMaintenanceProjectRequest) =>
      apiFetch('/maintenance/projects', maintenanceProjectSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ['maintenance', 'projects'] });
    },
  });
}

export function useUpdateMaintenanceProject(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateMaintenanceProjectRequest) =>
      apiFetch(`/maintenance/projects/${id}`, maintenanceProjectSchema, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ['maintenance', 'projects'] });
      await client.invalidateQueries({ queryKey: maintenanceProjectKey(id) });
    },
  });
}

export function useMaintenanceInstallations(query: MaintenanceInstallationListQuery) {
  const search = new URLSearchParams();
  search.set('page', String(query.page));
  search.set('pageSize', String(query.pageSize));
  search.set('sortBy', query.sortBy);
  search.set('sortOrder', query.sortOrder);
  if (query.q) {
    search.set('q', query.q);
  }
  if (query.projectId) {
    search.set('projectId', query.projectId);
  }
  if (query.customerId) {
    search.set('customerId', query.customerId);
  }
  if (query.isActive !== undefined) {
    search.set('isActive', String(query.isActive));
  }
  return useQuery({
    queryKey: maintenanceInstallationsKey(query),
    queryFn: () =>
      apiFetch(
        `/maintenance/installations?${search.toString()}`,
        maintenanceInstallationListResponseSchema,
      ),
    enabled: query.projectId === undefined || Boolean(query.projectId),
  });
}

export function useMaintenanceInstallation(id: string) {
  return useQuery({
    queryKey: maintenanceInstallationKey(id),
    queryFn: () => apiFetch(`/maintenance/installations/${id}`, maintenanceInstallationSchema),
    enabled: Boolean(id),
  });
}

export function useMaintenanceInstallationHistory(id: string) {
  return useQuery({
    queryKey: ['maintenance', 'installation-history', id] as const,
    queryFn: () =>
      apiFetch(`/maintenance/installations/${id}/history`, maintenanceInstallationHistorySchema),
    enabled: Boolean(id),
  });
}

export function useCreateMaintenanceInstallation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateMaintenanceInstallationRequest) =>
      apiFetch('/maintenance/installations', maintenanceInstallationSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ['maintenance', 'installations'] });
      await client.invalidateQueries({ queryKey: ['maintenance', 'projects'] });
    },
  });
}

export function useUpdateMaintenanceInstallation(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateMaintenanceInstallationRequest) =>
      apiFetch(`/maintenance/installations/${id}`, maintenanceInstallationSchema, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ['maintenance', 'installations'] });
      await client.invalidateQueries({ queryKey: maintenanceInstallationKey(id) });
    },
  });
}
